import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { Signer } from '@aws-amplify/core';
import { captureException } from '@sentry/react';

import { notifyCardCanceled as notifyCardCanceledMutation } from 'graphql_custom/cards/mutations';
import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';
import type {
  QueryHnGetBusinessAccountHolderArgs,
  QueryHnGetBusinessAccountHolderForPaymentCardArgs,
  QueryHnGetPaymentCardsForBusinessIdArgs,
  QueryHnGetPaymentCardDetailsArgs,
  QueryHnGetDetailsForPaymentCardArgs,
  QueryHnGetAccountHolderCardProductApplicationArgs,
  QueryHnViewDirectDepositDetailsArgs,
  QueryHnGetSpendRuleByIdArgs,
  QueryHnGetVelocityRuleByIdArgs,
  QueryHnFindFinancialAccountTransfersArgs,
  QueryHnFindFinancialAccountLedgerActivityArgs,
  QueryHnFindFinancialAccountActivitiesArgs,
  QueryHnGetDisputeDetailsArgs,
  HnActivatePaymentCardInput,
  HnSuspendPaymentCardInput,
  HnClosePaymentCardInput,
  HnGeneratePaymentCardClientTokenInput,
  HnCreateAmountLimitSpendRuleInput,
  HnCreateMerchantCategorySpendRuleInput,
  HnCreateMerchantCountrySpendRuleInput,
  HnCreateVelocityRuleInput,
  HnCreateOneTimeAchTransferInput,
  HnCreateRecurringAchTransferInput,
  HnCancelScheduledTransferInput,
  HnInitiateFundsDepositAchTransferInput,
  HnAddExternalBankAccountVerifiedThroughPlaidInput,
  HnAddExternalBankAccountVerifiedThroughFinicityInput,
  HnCloseExternalFinancialBankAccountInput,
  HnIssueFinancialAccountForApplicationInput,
  HnGenerateDirectDepositDetailClientTokenInput,
  HnCreateAccountHolderCardProductApplicationInput,
  HnStartDocumentUploadSessionInput,
  HnCreateDocumentUploadLinkInput,
  HnEndDocumentUploadSessionInput,
  HnBusinessAccountHolderResponse,
  HnBusinessAccountHolderForCardResponse,
  HnPaymentCardsForBusinessResponse,
  HnPaymentCardDetailsResponse,
  HnDetailsForPaymentCardResponse,
  HnApplicationResponse,
  HnDirectDepositDetailsResponse,
  HnSpendRuleResponse,
  HnVelocityRuleResponse,
  HnFinancialAccountTransfersResponse,
  HnLedgerActivityResponse,
  HnFinancialAccountActivitiesResponse,
  HnDisputeDetailsResponse,
  HnPaymentCardMutationResponse,
  HnClientTokenResponse,
  HnCreateAmountLimitSpendRuleResponse,
  HnCreateSpendRuleResponse,
  HnCreateVelocityRuleResponse,
  HnachTransferResponse,
  HnCancelScheduledTransferResponse,
  HnInitiateFundsDepositResponse,
  HnExternalBankAccountResponse,
  HnIssueFinancialAccountResponse,
  HnCreateApplicationResponse,
  HnDocumentUploadSessionResponse,
  HnDocumentUploadLinkResponse,
  HnValidateAddressInput,
  HnAddressValidationResponse,
  MutationNotifyCardCanceledArgs,
  NotifyCardCanceledResponse,
} from 'CDK_API';

/**
 * Relaxes all properties for the Amplify→CDK boundary:
 * - All fields become optional and nullable
 * - String literal unions (enums) are widened to string
 * - Recursively applies to nested objects and InputMaybe<T> unions
 * AppSync validates the actual GraphQL types at runtime.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeepPartial<T> = T extends any[]
  ? T
  : T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> | null }
  : T extends string
  ? string | null
  : T | null;

// --- GraphQL Operations ---
// Query/mutation strings use typed arguments and field selections matching CDK AppSync schema.

// Queries (13)
const hnGetBusinessAccountHolderQuery = /* GraphQL */ `
  query HnGetBusinessAccountHolder($id: String!) {
    hnGetBusinessAccountHolder(id: $id) {
      id
      businessProfile {
        id
        businessType
        name {
          doingBusinessAsName
          legalBusinessName
        }
        billingAddress {
          streetAddress
          extendedAddress
          postalCode
          region
          locality
          countryCodeAlpha3
        }
      }
      primaryAuthorizedPerson {
        id
        email
        authorizingPersonTitle
        dateOfBirth
        percentageOwnership
        name {
          givenName
          familyName
          middleName
          suffix
          title
        }
        homeAddress {
          streetAddress
          extendedAddress
          postalCode
          region
          locality
          countryCodeAlpha3
        }
      }
      financialAccounts {
        edges {
          node {
            id
            accountStatus
            activeBillingCycleConfiguration {
              id
              paymentDueDayOfMonth
              billingCycleStartDayOfMonth
              billingCycleEndDayOfMonth
              from
              through
            }
            directDepositDetails {
              id
            }
            ledgers {
              name
              normalBalance
              debitBalance {
                value
                currencyCode
              }
              creditBalance {
                value
                currencyCode
              }
            }
            statementSnapshot {
              currentAmountDue {
                value
                currencyCode
              }
              latestClosedStatement {
                id
                closedAt
                endingPrimaryCreditBalance {
                  value
                  currencyCode
                }
                paymentDueOn
                pastDueAmount {
                  value
                  currencyCode
                }
              }
              currentOpenStatement {
                id
                periodStart
                periodEnd
                openedAt
                closedAt
                paymentDueOn
                endingPrimaryCreditBalance {
                  value
                  currencyCode
                }
                startingPrimaryCreditBalance {
                  value
                  currencyCode
                }
              }
            }
            statements(first: 50) {
              edges {
                node {
                  id
                  periodStart
                  periodEnd
                  openedAt
                  closedAt
                  endingPrimaryCreditBalance {
                    value
                    currencyCode
                  }
                  startingPrimaryCreditBalance {
                    value
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
      externalFinancialAccounts {
        edges {
          node {
            id
            name
            accountStatus
            externalBankAccountDetails {
              id
              last4
              type
              routingNumber
            }
          }
        }
      }
      paymentCards {
        edges {
          cursor
          node {
            id
            last4
            expirationDate
          }
        }
      }
      cardProductApplications {
        edges {
          node {
            id
            applicationState {
              status
            }
            adverseActionReasons {
              code
              description
            }
            updatedAt
            createdAt
          }
        }
      }
    }
  }
`;

const hnGetBusinessAccountHolderForPaymentCardQuery = /* GraphQL */ `
  query HnGetBusinessAccountHolderForPaymentCard($id: ID!) {
    hnGetBusinessAccountHolderForPaymentCard(id: $id) {
      application {
        accountHolderSnapshot {
          accountHolderCurrent {
            id
          }
        }
      }
    }
  }
`;

const hnGetPaymentCardsForBusinessIdQuery = /* GraphQL */ `
  query HnGetPaymentCardsForBusinessId(
    $id: ID!
    $first: Int
    $after: String
    $filterBy: HNPaymentCardsFilterInput
  ) {
    hnGetPaymentCardsForBusinessId(id: $id, first: $first, after: $after, filterBy: $filterBy) {
      paymentCards {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            last4
            network
            expirationDate
            status
            formFactor
            cardProduct {
              name
            }
            attachedSpendRules {
              edges {
                cursor
                node {
                  id
                  name
                  maximumAmount {
                    value
                    currencyCode
                  }
                  merchantCategoryAllowed
                  merchantCategoryBlocked
                  merchantCountryAllowed
                  merchantCountryBlocked
                  CVVAllowed
                  CVVBlocked
                  streetAddressAllowed
                  streetAddressBlocked
                }
              }
            }
            attachedVelocityRules {
              edges {
                cursor
                node {
                  id
                  cumulativeRule {
                    id
                    name
                    maximumAmount {
                      value
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const hnGetPaymentCardDetailsQuery = /* GraphQL */ `
  query HnGetPaymentCardDetails($id: ID!) {
    hnGetPaymentCardDetails(id: $id) {
      id
      last4
      network
      expirationDate
      status
      formFactor
      paymentCardHolderDetails {
        billingAddressOnCard {
          streetAddress
          extendedAddress
          postalCode
          region
          locality
          countryCodeAlpha3
        }
      }
      cardProduct {
        name
      }
      attachedSpendRules {
        edges {
          cursor
          node {
            __typename
            id
            name
            maximumAmount {
              value
              currencyCode
            }
            merchantCategoryAllowed
            merchantCategoryBlocked
            merchantCountryAllowed
            merchantCountryBlocked
            CVVAllowed
            CVVBlocked
            streetAddressAllowed
            streetAddressBlocked
          }
        }
      }
      attachedVelocityRules {
        edges {
          cursor
          node {
            id
            cumulativeRule {
              id
              name
              maximumAmount {
                value
                currencyCode
              }
            }
          }
        }
      }
      velocitySpendRuleBalances {
        currentBalance {
          value
          currencyCode
        }
        remainingBalance {
          value
          currencyCode
        }
      }
      physicalPaymentCardOrders {
        id
        createdAt
        orderState {
          status
        }
        paymentCardShipment {
          requestedShipDate
          courier {
            method
            signatureRequiredOnDelivery
            tracking {
              trackingNumber
              actualShipDateLocal
            }
          }
        }
      }
    }
  }
`;

const hnGetDetailsForPaymentCardQuery = /* GraphQL */ `
  query HnGetDetailsForPaymentCard($id: ID!) {
    hnGetDetailsForPaymentCard(id: $id) {
      formFactor
      status
      cardProduct {
        name
      }
      velocitySpendRuleBalances {
        currentBalance {
          value
          currencyCode
        }
        remainingBalance {
          value
          currencyCode
        }
      }
      attachedVelocityRules {
        edges {
          cursor
          node {
            id
            cumulativeRule {
              id
              name
              version
              maximumAmount {
                value
                currencyCode
              }
              updatedAt
              createdAt
            }
          }
        }
      }
    }
  }
`;

const hnGetAccountHolderCardProductApplicationQuery = /* GraphQL */ `
  query HnGetAccountHolderCardProductApplication($id: ID!) {
    hnGetAccountHolderCardProductApplication(id: $id) {
      id
      createdAt
      updatedAt
      applicationState {
        status
      }
      cardProduct {
        name
      }
      adverseActionReasons {
        code
        description
      }
      accountHolderSnapshot {
        name {
          givenName
          familyName
          title
          suffix
        }
        primaryAuthorizedPerson {
          name {
            givenName
            familyName
            title
            suffix
          }
          currentVerification {
            status
            reason
            results {
              code
              description
            }
            requiredDocuments {
              createdAt
              referenceIdentifier
              updatedAt
              status
              documentUploadSession {
                id
                status
                primaryDocumentTypes
                secondaryDocumentTypes
                uploadRequirements {
                  constraints {
                    documentType
                    numberOfDocuments
                  }
                }
              }
              uploadedDocuments {
                status
                type
                createdAt
                updatedAt
              }
            }
          }
        }
        businessProfile {
          name {
            doingBusinessAsName
            legalBusinessName
          }
          currentCreditUnderwritingVerification {
            status
            decision {
              creditLine {
                value
                currencyCode
              }
              adverseActionReasons {
                code
                description
              }
            }
          }
          currentVerification {
            status
            reason
            results {
              code
              description
            }
            requiredDocuments {
              createdAt
              referenceIdentifier
              updatedAt
              status
              documentUploadSession {
                id
                status
                primaryDocumentTypes
                secondaryDocumentTypes
                uploadRequirements {
                  constraints {
                    documentType
                    numberOfDocuments
                  }
                }
              }
              uploadedDocuments {
                status
                type
                createdAt
                updatedAt
              }
            }
          }
          ultimateBeneficialOwners {
            name {
              givenName
              familyName
              title
              suffix
            }
            currentVerification {
              status
              reason
              results {
                code
                description
              }
              requiredDocuments {
                createdAt
                referenceIdentifier
                updatedAt
                status
                documentUploadSession {
                  id
                  status
                  primaryDocumentTypes
                  secondaryDocumentTypes
                  uploadRequirements {
                    constraints {
                      documentType
                      numberOfDocuments
                    }
                  }
                }
                uploadedDocuments {
                  status
                  type
                  createdAt
                  updatedAt
                }
              }
            }
          }
        }
      }
      applicationHistory {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            createdAt
            updatedAt
            applicationState {
              status
            }
          }
        }
      }
    }
  }
`;

const hnViewDirectDepositDetailsQuery = /* GraphQL */ `
  query HnViewDirectDepositDetails($id: ID!) {
    hnViewDirectDepositDetails(id: $id) {
      id
      restrictedDetails {
        __typename
        number
        routingNumber
        type
        message
        bank {
          name
          address {
            streetAddress
            postalCode
          }
        }
      }
    }
  }
`;

const hnGetSpendRuleByIdQuery = /* GraphQL */ `
  query HnGetSpendRuleById($ruleId: ID!) {
    hnGetSpendRuleById(ruleId: $ruleId) {
      id
      name
      version
      createdAt
      updatedAt
      maximumAmount {
        value
        currencyCode
      }
      allowedCategory
      blockedCategory
      allowedCountry
      blockedCountry
    }
  }
`;

const hnGetVelocityRuleByIdQuery = /* GraphQL */ `
  query HnGetVelocityRuleById($ruleId: ID!) {
    hnGetVelocityRuleById(ruleId: $ruleId) {
      id
      name
      version
      velocityRuleWindow
      createdAt
      updatedAt
      cumulativeRule {
        id
      }
      spendRules {
        id
      }
    }
  }
`;

const hnFindFinancialAccountTransfersQuery = /* GraphQL */ `
  query HnFindFinancialAccountTransfers(
    $id: ID!
    $firstScheduledTransfers: Int
    $scheduledTransfersAfterCursor: String
    $firstIntegratorTransfers: Int
    $integratorTransfersAfterCursor: String
    $firstExternalTransfers: Int
    $externalTransfersAfterCursor: String
  ) {
    hnFindFinancialAccountTransfers(
      id: $id
      firstScheduledTransfers: $firstScheduledTransfers
      scheduledTransfersAfterCursor: $scheduledTransfersAfterCursor
      firstIntegratorTransfers: $firstIntegratorTransfers
      integratorTransfersAfterCursor: $integratorTransfersAfterCursor
      firstExternalTransfers: $firstExternalTransfers
      externalTransfersAfterCursor: $externalTransfersAfterCursor
    ) {
      id
      name
      incomingScheduledTransfers {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            __typename
            id
            status
            descriptor {
              companyEntryDescription
              individualName
              individualIdentificationNumber
            }
            transferAmount {
              calculatedType
              amount {
                value
                currencyCode
              }
            }
            scheduledTransferDate
            nextScheduledTransferDate
            frequency
          }
        }
      }
      integratorACHTransfers {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            __typename
            id
            companyIdentifier
            companyName
            settlementDate
            amount {
              value
              currencyCode
            }
            descriptor {
              companyEntryDescription
              individualName
              individualIdentificationNumber
            }
            status {
              status
              statusReasonCode
            }
          }
        }
      }
      externalACHTransfers {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            __typename
            id
            companyIdentifier
            companyName
            settlementDate
            amount {
              value
              currencyCode
            }
            descriptor {
              companyEntryDescription
              individualName
              individualIdentificationNumber
            }
            status {
              status
              statusReasonCode
            }
          }
        }
      }
    }
  }
`;

const hnFindFinancialAccountLedgerActivityQuery = /* GraphQL */ `
  query HnFindFinancialAccountLedgerActivity(
    $id: ID!
    $firstActivities: Int
    $activitiesAfterCursor: String
  ) {
    hnFindFinancialAccountLedgerActivity(
      id: $id
      firstActivities: $firstActivities
      activitiesAfterCursor: $activitiesAfterCursor
    ) {
      id
      ledgers {
        id
        name
        normalBalance
        asOf
        creditBalance {
          value
          currencyCode
          decimalPlaces
        }
        debitBalance {
          value
          currencyCode
          decimalPlaces
        }
        ledgerEntries {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            cursor
            node {
              id
              memo
              createdAt
              creditBalanceAtLedgerEntry {
                value
                currencyCode
                decimalPlaces
              }
              debitBalanceAtLedgerEntry {
                value
                currencyCode
                decimalPlaces
              }
              amount {
                value
                currencyCode
                decimalPlaces
              }
              financialEvent {
                id
                memo
                purpose
                merchantDetails {
                  name
                  category
                }
                externallyInitiatedACHStatus {
                  status
                  statusReasonCode
                }
                integratorInitiatedACHStatus {
                  status
                  statusReasonCode
                }
                toFinancialAccount {
                  id
                  name
                }
                fromFinancialAccount {
                  id
                  name
                }
                feeTransfer {
                  feeActivityType
                  sourceFeeTransferEvent {
                    feeTransfer {
                      feeActivityType
                    }
                  }
                }
                adjustmentActivityType
                accountingDirection
                balanceType
                interestDetails {
                  apr
                }
                statement {
                  id
                }
                eventType
                type
                rewardPointsValue {
                  value
                  currencyCode
                }
                rewardPointsAmount {
                  value
                  currencyCode
                }
                source
                externalTransactionIdentifier
                activity
                status {
                  status
                  statusReasonCode
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Only an InterFinancialAccountTransfer carries these. Highnote leaves purpose
 * null for the kinds that are not payments (SET_LIMIT, the DISPUTE_* family,
 * CLOSE_LINE_OF_CREDIT), and every other source kind has no purpose at all,
 * which is how the activity list tells a repayment from a credit-limit change
 * (SEE-1638).
 */
let activitySourceSupportsClassification = true;

/**
 * When the downgrade above was last taken. It is retried after this long so a tab
 * opened during the deploy window (r2 live, CDK not yet) starts classifying again
 * once the CDK ships, instead of staying degraded until the user hard-refreshes.
 * One wasted request per window is cheaper than an activity list that silently
 * stays wrong for the rest of the session.
 */
let classificationDowngradedAt = 0;
const CLASSIFICATION_RETRY_AFTER_MS = 5 * 60 * 1000;

/**
 * AppSync's shape for "you asked for a field this schema does not declare".
 *
 * Read `message` rather than stringifying: JSON.stringify of an Error is `{}`,
 * because its properties are not enumerable.
 */
export function isUndefinedFieldError(error: unknown): boolean {
  const text = error instanceof Error ? error.message : String(error ?? '');
  return text.includes('FieldUndefined') && (text.includes('purpose') || text.includes('memo'));
}

const sourceClassificationFields = `
              purpose
              memo`;

const buildFindFinancialAccountActivitiesQuery = (classificationFields: string) => /* GraphQL */ `
  query HnFindFinancialAccountActivities(
    $id: ID!
    $firstActivities: Int
    $filterBy: HNFinancialAccountActivitiesFilterInput
    $activitiesAfterCursor: String
  ) {
    hnFindFinancialAccountActivities(
      id: $id
      firstActivities: $firstActivities
      filterBy: $filterBy
      activitiesAfterCursor: $activitiesAfterCursor
    ) {
      id
      financialAccountActivities {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            sign
            isComplete
            createdAt
            pendingAmount {
              value
              currencyCode
              decimalPlaces
            }
            postedAmount {
              value
              currencyCode
              decimalPlaces
            }
            source {
              id
              ${classificationFields}
              transactionEvents {
                id
                paymentCard {
                  id
                  last4
                }
                merchantDetails {
                  name
                  category
                }
                pointOfServiceDetails {
                  isCardPresent
                }
              }
            }
          }
        }
      }
    }
  }
`;

// SEE-1232 row q. Source: src/graphql_custom/queries.js
// (listHighnoteDisputeEventsByPaidolIdCustom). Two argument changes against the Amplify
// version: `$eventDate: ModelStringKeyConditionInput` is dropped — the document declared
// and forwarded it but Credit.tsx never supplied a value — and `$sortDirection` is CDK's
// `SortDirection` rather than `ModelSortDirection`. The caller passes 'DESC' as a string
// literal, so no call-site change follows from the enum rename. `nextToken` is an opaque
// base64 offset here, not a DynamoDB LastEvaluatedKey; the caller's loop-until-null is
// unaffected. `rawEventData` is deliberately absent from the CDK field and was never
// selected here.
const listHighnoteDisputeEventsByPaidolIdQuery = /* GraphQL */ `
  query ListHighnoteDisputeEventsByPaidolId(
    $paidolId: ID!
    $sortDirection: SortDirection
    $limit: Int
    $nextToken: String
  ) {
    listHighnoteDisputeEventsByPaidolId(
      paidolId: $paidolId
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        disputeId
        transactionEventId
        amount {
          value
          currencyCode
        }
        status
        creditStatus
        category
        liability
        enableListing
        eventType
        eventDate
      }
      nextToken
    }
  }
`;

const hnGetDisputeDetailsQuery = /* GraphQL */ `
  query HnGetDisputeDetails($id: ID!) {
    hnGetDisputeDetails(id: $id) {
      id
      status
      category
      liability
      createdAt
      updatedAt
      amount {
        value
        currencyCode
      }
      transactionEvent {
        id
      }
      chargebacks {
        id
        requestedAmount {
          value
          currencyCode
        }
        receivedAmount {
          value
          currencyCode
        }
      }
      disputeCreditHistory {
        creditStatus
        creditAmount {
          value
          currencyCode
        }
        createdAt
        updatedAt
      }
    }
  }
`;

// Mutations (22)
const hnActivatePaymentCardMutation = /* GraphQL */ `
  mutation HnActivatePaymentCard($input: HNActivatePaymentCardInput!) {
    hnActivatePaymentCard(input: $input) {
      id
      status
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnSuspendPaymentCardMutation = /* GraphQL */ `
  mutation HnSuspendPaymentCard($input: HNSuspendPaymentCardInput!) {
    hnSuspendPaymentCard(input: $input) {
      id
      status
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnClosePaymentCardMutation = /* GraphQL */ `
  mutation HnClosePaymentCard($input: HNClosePaymentCardInput!) {
    hnClosePaymentCard(input: $input) {
      id
      status
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnGenerateTokenizeAccountHolderClientTokenMutation = /* GraphQL */ `
  mutation HnGenerateTokenizeAccountHolderClientToken {
    hnGenerateTokenizeAccountHolderClientToken {
      value
      expirationDate
      usage
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnGeneratePaymentCardClientTokenMutation = /* GraphQL */ `
  mutation HnGeneratePaymentCardClientToken($input: HNGeneratePaymentCardClientTokenInput!) {
    hnGeneratePaymentCardClientToken(input: $input) {
      value
      expirationDate
      usage
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCreateAmountLimitSpendRuleMutation = /* GraphQL */ `
  mutation HnCreateAmountLimitSpendRule($input: HNCreateAmountLimitSpendRuleInput!) {
    hnCreateAmountLimitSpendRule(input: $input) {
      id
      name
      version
      maximumAmount {
        value
        currencyCode
      }
      createdAt
      updatedAt
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCreateMerchantCategorySpendRuleMutation = /* GraphQL */ `
  mutation HnCreateMerchantCategorySpendRule($input: HNCreateMerchantCategorySpendRuleInput!) {
    hnCreateMerchantCategorySpendRule(input: $input) {
      id
      name
      version
      allowed
      blocked
      createdAt
      updatedAt
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCreateMerchantCountrySpendRuleMutation = /* GraphQL */ `
  mutation HnCreateMerchantCountrySpendRule($input: HNCreateMerchantCountrySpendRuleInput!) {
    hnCreateMerchantCountrySpendRule(input: $input) {
      id
      name
      version
      allowed
      blocked
      createdAt
      updatedAt
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCreateVelocityRuleMutation = /* GraphQL */ `
  mutation HnCreateVelocityRule($input: HNCreateVelocityRuleInput!) {
    hnCreateVelocityRule(input: $input) {
      id
      name
      version
      velocityRuleWindow
      createdAt
      updatedAt
      cumulativeRule {
        id
      }
      spendRules {
        id
      }
      errors {
        code
        description
      }
    }
  }
`;

const hnCreateOneTimeACHTransferMutation = /* GraphQL */ `
  mutation HnCreateOneTimeACHTransfer($input: HNCreateOneTimeACHTransferInput!) {
    hnCreateOneTimeACHTransfer(input: $input) {
      id
      status
      createdAt
      updatedAt
      descriptor {
        companyEntryDescription
        individualName
        individualIdentificationNumber
      }
      transferAmount {
        calculatedType
        amount {
          value
          currencyCode
        }
      }
      fromFinancialAccount {
        id
      }
      toFinancialAccount {
        id
      }
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCreateRecurringACHTransferMutation = /* GraphQL */ `
  mutation HnCreateRecurringACHTransfer($input: HNCreateRecurringACHTransferInput!) {
    hnCreateRecurringACHTransfer(input: $input) {
      id
      status
      createdAt
      updatedAt
      descriptor {
        companyEntryDescription
        individualName
        individualIdentificationNumber
      }
      transferAmount {
        calculatedType
        amount {
          value
          currencyCode
        }
      }
      fromFinancialAccount {
        id
      }
      toFinancialAccount {
        id
      }
      frequency
      nextScheduledTransferDate
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCancelScheduledTransferMutation = /* GraphQL */ `
  mutation HnCancelScheduledTransfer($input: HNCancelScheduledTransferInput!) {
    hnCancelScheduledTransfer(input: $input) {
      id
      status
      frequency
      nextScheduledTransferDate
      createdAt
      updatedAt
      descriptor {
        companyEntryDescription
        individualName
        individualIdentificationNumber
      }
      transferAmount {
        calculatedType
        amount {
          value
          currencyCode
        }
      }
      fromFinancialAccount {
        id
      }
      toFinancialAccount {
        id
      }
    }
  }
`;

const hnInitiateFundsDepositACHTransferMutation = /* GraphQL */ `
  mutation HnInitiateFundsDepositACHTransfer($input: HNInitiateFundsDepositACHTransferInput!) {
    hnInitiateFundsDepositACHTransfer(input: $input) {
      id
      createdAt
      updatedAt
      companyName
      descriptor {
        companyEntryDescription
        individualName
        individualIdentificationNumber
      }
      amount {
        value
        currencyCode
      }
      fromFinancialAccount {
        id
      }
      toFinancialAccount {
        id
      }
      status {
        status
        statusReasonCode
      }
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnAddExternalBankAccountVerifiedThroughPlaidMutation = /* GraphQL */ `
  mutation HnAddExternalBankAccountVerifiedThroughPlaid(
    $input: HNAddExternalBankAccountVerifiedThroughPlaidInput!
  ) {
    hnAddExternalBankAccountVerifiedThroughPlaid(input: $input) {
      id
      name
      provider
      accountStatus
      createdAt
      updatedAt
      ledgers {
        id
        name
        description
        normalBalance
        asOf
      }
      owner {
        id
        email
        dateOfBirth
        externalId
        createdAt
        updatedAt
        bin
        last4
        expirationDate
        expirationMonth
        expirationYear
        network
        status
        formFactor
      }
      features {
        enabled
        createdAt
        updatedAt
      }
      externalBankAccountDetails {
        id
        last4
        type
        routingNumber
        createdAt
        updatedAt
      }
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnAddExternalBankAccountVerifiedThroughFinicityMutation = /* GraphQL */ `
  mutation HnAddExternalBankAccountVerifiedThroughFinicity(
    $input: HNAddExternalBankAccountVerifiedThroughFinicityInput!
  ) {
    hnAddExternalBankAccountVerifiedThroughFinicity(input: $input) {
      id
      name
      provider
      accountStatus
      createdAt
      updatedAt
      ledgers {
        id
        name
        description
        normalBalance
        asOf
      }
      owner {
        id
        email
        dateOfBirth
        externalId
        createdAt
        updatedAt
        bin
        last4
        expirationDate
        expirationMonth
        expirationYear
        network
        status
        formFactor
      }
      features {
        enabled
        createdAt
        updatedAt
      }
      externalBankAccountDetails {
        id
        last4
        type
        routingNumber
        createdAt
        updatedAt
      }
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnCloseExternalFinancialBankAccountMutation = /* GraphQL */ `
  mutation HnCloseExternalFinancialBankAccount($input: HNCloseExternalFinancialBankAccountInput!) {
    hnCloseExternalFinancialBankAccount(input: $input) {
      id
      name
      accountStatus
      createdAt
      updatedAt
      ledgers {
        id
        name
      }
      features {
        enabled
      }
      externalBankAccountDetails {
        id
        last4
        type
        routingNumber
        createdAt
        updatedAt
      }
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnIssueFinancialAccountForApplicationMutation = /* GraphQL */ `
  mutation HnIssueFinancialAccountForApplication($input: HNIssueFinancialAccountForApplicationInput!) {
    hnIssueFinancialAccountForApplication(input: $input) {
      id
      externalId
      name
      createdAt
      updatedAt
      application {
        id
        createdAt
      }
      cardProduct {
        id
        vertical
      }
      features {
        enabled
        createdAt
        updatedAt
      }
      directDepositDetails {
        id
        restrictedDetails {
          number
          routingNumber
          bank {
            name
          }
        }
      }
    }
  }
`;

const hnGenerateDirectDepositDetailClientTokenMutation = /* GraphQL */ `
  mutation HnGenerateDirectDepositDetailClientToken($input: HNGenerateDirectDepositDetailClientTokenInput!) {
    hnGenerateDirectDepositDetailClientToken(input: $input) {
      value
      expirationDate
      usage
      errors {
        code
        description
      }
      message
    }
  }
`;

const hnCreateAccountHolderCardProductApplicationMutation = /* GraphQL */ `
  mutation HnCreateAccountHolderCardProductApplication(
    $input: HNCreateAccountHolderCardProductApplicationInput!
  ) {
    hnCreateAccountHolderCardProductApplication(input: $input) {
      id
      applicationState {
        status
      }
      updatedAt
      createdAt
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

const hnStartDocumentUploadSessionMutation = /* GraphQL */ `
  mutation HnStartDocumentUploadSession($input: HNStartDocumentUploadSessionInput!) {
    hnStartDocumentUploadSession(input: $input) {
      id
      status
      primaryDocumentTypes
      secondaryDocumentTypes
      supportingDocumentTypes
      uploadRequirements {
        constraints {
          documentType
          numberOfDocuments
        }
      }
      documents {
        id
        status
        documentType
        documentCategoryType
      }
    }
  }
`;

const hnCreateDocumentUploadLinkMutation = /* GraphQL */ `
  mutation HnCreateDocumentUploadLink($input: HNCreateDocumentUploadLinkInput!) {
    hnCreateDocumentUploadLink(input: $input) {
      id
      documentType
      documentCategoryType
      status
      uploadUrl
      documentUploadSession {
        id
        status
      }
    }
  }
`;

const hnEndDocumentUploadSessionMutation = /* GraphQL */ `
  mutation HnEndDocumentUploadSession($input: HNEndDocumentUploadSessionInput!) {
    hnEndDocumentUploadSession(input: $input) {
      id
      status
      documents {
        id
        status
        documentType
        documentCategoryType
      }
    }
  }
`;

const hnValidateAddressMutation = /* GraphQL */ `
  mutation HnValidateAddress($input: HNValidateAddressInput!) {
    hnValidateAddress(input: $input) {
      status
      labels
      correctedAddress {
        streetAddress
        extendedAddress
        postalCode
        locality
        region
        countryCodeAlpha3
      }
      componentsChanged
      errors {
        path
        code
        description
      }
      message
    }
  }
`;

// Health check
const v2HighnoteHealthCheckQuery = /* GraphQL */ `
  query V2HighnoteHealthCheck($input: String) {
    v2HighnoteHealthCheck(input: $input)
  }
`;

// --- Service ---

// Matches Amplify's "no Cognito session" errors only — other failures
// (network, refresh-token, misconfig) should surface so authenticated users
// aren't silently downgraded to IAM scope.
function isNoCognitoSessionError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /no current user|not authenticated|user is not authenticated/i.test(message);
}

/**
 * Service for v2Highnote operations via the CDK AppSync API.
 *
 * Uses fetch() + Cognito JWT to call the CDK-managed AppSync endpoint.
 * Query arguments are typed (matching the CDK schema); returns are AWSJSON
 * (the Lambda returns JSON blobs from the Highnote API).
 */
export class HighnoteService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  // --- Queries (variables passed directly — flat args) ---

  async hnGetBusinessAccountHolder(
    variables: DeepPartial<QueryHnGetBusinessAccountHolderArgs>
  ): Promise<HnBusinessAccountHolderResponse> {
    return this.executeAndParse<HnBusinessAccountHolderResponse>(
      hnGetBusinessAccountHolderQuery,
      variables,
      'hnGetBusinessAccountHolder'
    );
  }

  async hnGetBusinessAccountHolderForPaymentCard(
    variables: DeepPartial<QueryHnGetBusinessAccountHolderForPaymentCardArgs>
  ): Promise<HnBusinessAccountHolderForCardResponse> {
    return this.executeAndParse<HnBusinessAccountHolderForCardResponse>(
      hnGetBusinessAccountHolderForPaymentCardQuery,
      variables,
      'hnGetBusinessAccountHolderForPaymentCard'
    );
  }

  async hnGetPaymentCardsForBusinessId(
    variables: DeepPartial<QueryHnGetPaymentCardsForBusinessIdArgs>
  ): Promise<HnPaymentCardsForBusinessResponse> {
    return this.executeAndParse<HnPaymentCardsForBusinessResponse>(
      hnGetPaymentCardsForBusinessIdQuery,
      variables,
      'hnGetPaymentCardsForBusinessId'
    );
  }

  async hnGetPaymentCardDetails(
    variables: DeepPartial<QueryHnGetPaymentCardDetailsArgs>
  ): Promise<HnPaymentCardDetailsResponse> {
    return this.executeAndParse<HnPaymentCardDetailsResponse>(
      hnGetPaymentCardDetailsQuery,
      variables,
      'hnGetPaymentCardDetails'
    );
  }

  async hnGetDetailsForPaymentCard(
    variables: DeepPartial<QueryHnGetDetailsForPaymentCardArgs>
  ): Promise<HnDetailsForPaymentCardResponse> {
    return this.executeAndParse<HnDetailsForPaymentCardResponse>(
      hnGetDetailsForPaymentCardQuery,
      variables,
      'hnGetDetailsForPaymentCard'
    );
  }

  async hnGetAccountHolderCardProductApplication(
    variables: DeepPartial<QueryHnGetAccountHolderCardProductApplicationArgs>
  ): Promise<HnApplicationResponse> {
    return this.executeAndParse<HnApplicationResponse>(
      hnGetAccountHolderCardProductApplicationQuery,
      variables,
      'hnGetAccountHolderCardProductApplication'
    );
  }

  async hnViewDirectDepositDetails(
    variables: DeepPartial<QueryHnViewDirectDepositDetailsArgs> & { _bearerToken?: string }
  ): Promise<HnDirectDepositDetailsResponse> {
    const { _bearerToken, ...queryVariables } = variables;
    const customHeaders = _bearerToken ? { customauthorization: `Bearer ${_bearerToken}` } : undefined;
    return this.executeAndParse<HnDirectDepositDetailsResponse>(
      hnViewDirectDepositDetailsQuery,
      queryVariables,
      'hnViewDirectDepositDetails',
      customHeaders
    );
  }

  async hnGetSpendRuleById(
    variables: DeepPartial<QueryHnGetSpendRuleByIdArgs>
  ): Promise<HnSpendRuleResponse> {
    return this.executeAndParse<HnSpendRuleResponse>(
      hnGetSpendRuleByIdQuery,
      variables,
      'hnGetSpendRuleById'
    );
  }

  async hnGetVelocityRuleById(
    variables: DeepPartial<QueryHnGetVelocityRuleByIdArgs>
  ): Promise<HnVelocityRuleResponse> {
    return this.executeAndParse<HnVelocityRuleResponse>(
      hnGetVelocityRuleByIdQuery,
      variables,
      'hnGetVelocityRuleById'
    );
  }

  async hnFindFinancialAccountTransfers(
    variables: DeepPartial<QueryHnFindFinancialAccountTransfersArgs>
  ): Promise<HnFinancialAccountTransfersResponse> {
    return this.executeAndParse<HnFinancialAccountTransfersResponse>(
      hnFindFinancialAccountTransfersQuery,
      variables,
      'hnFindFinancialAccountTransfers'
    );
  }

  async hnFindFinancialAccountLedgerActivity(
    variables: DeepPartial<QueryHnFindFinancialAccountLedgerActivityArgs>
  ): Promise<HnLedgerActivityResponse> {
    return this.executeAndParse<HnLedgerActivityResponse>(
      hnFindFinancialAccountLedgerActivityQuery,
      variables,
      'hnFindFinancialAccountLedgerActivity'
    );
  }

  /**
   * Activity, including the fields that say what kind of transfer each row is.
   *
   * Those fields need the matching CDK schema. If the API is older, AppSync
   * rejects the whole query and the activity list would go blank rather than
   * simply losing the classification, so fall back once and remember. Deploy
   * order then stops being able to take the page down.
   */
  async hnFindFinancialAccountActivities(
    variables: DeepPartial<QueryHnFindFinancialAccountActivitiesArgs>
  ): Promise<HnFinancialAccountActivitiesResponse> {
    const run = (classificationFields: string) =>
      this.executeAndParse<HnFinancialAccountActivitiesResponse>(
        buildFindFinancialAccountActivitiesQuery(classificationFields),
        variables,
        'hnFindFinancialAccountActivities'
      );

    if (
      !activitySourceSupportsClassification &&
      Date.now() - classificationDowngradedAt < CLASSIFICATION_RETRY_AFTER_MS
    ) {
      return run('');
    }

    try {
      const result = await run(sourceClassificationFields);
      activitySourceSupportsClassification = true;
      return result;
    } catch (error) {
      if (!isUndefinedFieldError(error)) throw error;
      activitySourceSupportsClassification = false;
      classificationDowngradedAt = Date.now();
      captureException(
        new Error(
          'HNActivitySource has no purpose/memo: the CDK API predates SEE-1638, so internal transfers stay hidden until it is deployed'
        )
      );
      return run('');
    }
  }

  async hnGetDisputeDetails(
    variables: DeepPartial<QueryHnGetDisputeDetailsArgs>
  ): Promise<HnDisputeDetailsResponse> {
    return this.executeAndParse<HnDisputeDetailsResponse>(
      hnGetDisputeDetailsQuery,
      variables,
      'hnGetDisputeDetails'
    );
  }

  // --- Mutations (variables wrapped in { input } — typed input types) ---

  async hnActivatePaymentCard(
    input: DeepPartial<HnActivatePaymentCardInput>
  ): Promise<HnPaymentCardMutationResponse> {
    return this.executeAndParse<HnPaymentCardMutationResponse>(
      hnActivatePaymentCardMutation,
      { input },
      'hnActivatePaymentCard'
    );
  }

  async hnSuspendPaymentCard(
    input: DeepPartial<HnSuspendPaymentCardInput>
  ): Promise<HnPaymentCardMutationResponse> {
    return this.executeAndParse<HnPaymentCardMutationResponse>(
      hnSuspendPaymentCardMutation,
      { input },
      'hnSuspendPaymentCard'
    );
  }

  async hnClosePaymentCard(
    input: DeepPartial<HnClosePaymentCardInput>
  ): Promise<HnPaymentCardMutationResponse> {
    return this.executeAndParse<HnPaymentCardMutationResponse>(
      hnClosePaymentCardMutation,
      { input },
      'hnClosePaymentCard'
    );
  }

  /**
   * SEE-186: email the card's users that it was canceled. Only the card id is
   * sent; the server derives recipients, subject and body, and refuses unless
   * the caller is the user who just closed the card.
   */
  async notifyCardCanceled(args: MutationNotifyCardCanceledArgs): Promise<NotifyCardCanceledResponse> {
    return this.executeAndParse<NotifyCardCanceledResponse>(
      notifyCardCanceledMutation,
      args,
      'notifyCardCanceled'
    );
  }

  async hnGenerateTokenizeAccountHolderClientToken(): Promise<HnClientTokenResponse> {
    return this.executeAndParse<HnClientTokenResponse>(
      hnGenerateTokenizeAccountHolderClientTokenMutation,
      {},
      'hnGenerateTokenizeAccountHolderClientToken'
    );
  }

  async hnGeneratePaymentCardClientToken(
    input: DeepPartial<HnGeneratePaymentCardClientTokenInput>
  ): Promise<HnClientTokenResponse> {
    return this.executeAndParse<HnClientTokenResponse>(
      hnGeneratePaymentCardClientTokenMutation,
      { input },
      'hnGeneratePaymentCardClientToken'
    );
  }

  async hnCreateAmountLimitSpendRule(
    input: DeepPartial<HnCreateAmountLimitSpendRuleInput>
  ): Promise<HnCreateAmountLimitSpendRuleResponse> {
    return this.executeAndParse<HnCreateAmountLimitSpendRuleResponse>(
      hnCreateAmountLimitSpendRuleMutation,
      { input },
      'hnCreateAmountLimitSpendRule'
    );
  }

  async hnCreateMerchantCategorySpendRule(
    input: DeepPartial<HnCreateMerchantCategorySpendRuleInput>
  ): Promise<HnCreateSpendRuleResponse> {
    return this.executeAndParse<HnCreateSpendRuleResponse>(
      hnCreateMerchantCategorySpendRuleMutation,
      { input },
      'hnCreateMerchantCategorySpendRule'
    );
  }

  async hnCreateMerchantCountrySpendRule(
    input: DeepPartial<HnCreateMerchantCountrySpendRuleInput>
  ): Promise<HnCreateSpendRuleResponse> {
    return this.executeAndParse<HnCreateSpendRuleResponse>(
      hnCreateMerchantCountrySpendRuleMutation,
      { input },
      'hnCreateMerchantCountrySpendRule'
    );
  }

  async hnCreateVelocityRule(
    input: DeepPartial<HnCreateVelocityRuleInput>
  ): Promise<HnCreateVelocityRuleResponse> {
    return this.executeAndParse<HnCreateVelocityRuleResponse>(
      hnCreateVelocityRuleMutation,
      { input },
      'hnCreateVelocityRule'
    );
  }

  async hnCreateOneTimeACHTransfer(
    input: DeepPartial<HnCreateOneTimeAchTransferInput>
  ): Promise<HnachTransferResponse> {
    return this.executeAndParse<HnachTransferResponse>(
      hnCreateOneTimeACHTransferMutation,
      { input },
      'hnCreateOneTimeACHTransfer'
    );
  }

  async hnCreateRecurringACHTransfer(
    input: DeepPartial<HnCreateRecurringAchTransferInput>
  ): Promise<HnachTransferResponse> {
    return this.executeAndParse<HnachTransferResponse>(
      hnCreateRecurringACHTransferMutation,
      { input },
      'hnCreateRecurringACHTransfer'
    );
  }

  async hnCancelScheduledTransfer(
    input: DeepPartial<HnCancelScheduledTransferInput>
  ): Promise<HnCancelScheduledTransferResponse> {
    return this.executeAndParse<HnCancelScheduledTransferResponse>(
      hnCancelScheduledTransferMutation,
      { input },
      'hnCancelScheduledTransfer'
    );
  }

  async hnInitiateFundsDepositACHTransfer(
    input: DeepPartial<HnInitiateFundsDepositAchTransferInput>
  ): Promise<HnInitiateFundsDepositResponse> {
    return this.executeAndParse<HnInitiateFundsDepositResponse>(
      hnInitiateFundsDepositACHTransferMutation,
      { input },
      'hnInitiateFundsDepositACHTransfer'
    );
  }

  async hnAddExternalBankAccountVerifiedThroughPlaid(
    input: DeepPartial<HnAddExternalBankAccountVerifiedThroughPlaidInput>
  ): Promise<HnExternalBankAccountResponse> {
    return this.executeAndParse<HnExternalBankAccountResponse>(
      hnAddExternalBankAccountVerifiedThroughPlaidMutation,
      { input },
      'hnAddExternalBankAccountVerifiedThroughPlaid'
    );
  }

  async hnAddExternalBankAccountVerifiedThroughFinicity(
    input: DeepPartial<HnAddExternalBankAccountVerifiedThroughFinicityInput>
  ): Promise<HnExternalBankAccountResponse> {
    return this.executeAndParse<HnExternalBankAccountResponse>(
      hnAddExternalBankAccountVerifiedThroughFinicityMutation,
      { input },
      'hnAddExternalBankAccountVerifiedThroughFinicity'
    );
  }

  async hnCloseExternalFinancialBankAccount(
    input: DeepPartial<HnCloseExternalFinancialBankAccountInput>
  ): Promise<HnExternalBankAccountResponse> {
    return this.executeAndParse<HnExternalBankAccountResponse>(
      hnCloseExternalFinancialBankAccountMutation,
      { input },
      'hnCloseExternalFinancialBankAccount'
    );
  }

  async hnIssueFinancialAccountForApplication(
    input: DeepPartial<HnIssueFinancialAccountForApplicationInput>
  ): Promise<HnIssueFinancialAccountResponse> {
    return this.executeAndParse<HnIssueFinancialAccountResponse>(
      hnIssueFinancialAccountForApplicationMutation,
      { input },
      'hnIssueFinancialAccountForApplication'
    );
  }

  async hnGenerateDirectDepositDetailClientToken(
    input: DeepPartial<HnGenerateDirectDepositDetailClientTokenInput>
  ): Promise<HnClientTokenResponse> {
    return this.executeAndParse<HnClientTokenResponse>(
      hnGenerateDirectDepositDetailClientTokenMutation,
      { input },
      'hnGenerateDirectDepositDetailClientToken'
    );
  }

  async hnCreateAccountHolderCardProductApplication(
    input: DeepPartial<HnCreateAccountHolderCardProductApplicationInput>
  ): Promise<HnCreateApplicationResponse> {
    return this.executeAndParse<HnCreateApplicationResponse>(
      hnCreateAccountHolderCardProductApplicationMutation,
      { input },
      'hnCreateAccountHolderCardProductApplication'
    );
  }

  async hnStartDocumentUploadSession(
    input: DeepPartial<HnStartDocumentUploadSessionInput>
  ): Promise<HnDocumentUploadSessionResponse> {
    return this.executeAndParse<HnDocumentUploadSessionResponse>(
      hnStartDocumentUploadSessionMutation,
      { input },
      'hnStartDocumentUploadSession'
    );
  }

  async hnCreateDocumentUploadLink(
    input: DeepPartial<HnCreateDocumentUploadLinkInput>
  ): Promise<HnDocumentUploadLinkResponse> {
    return this.executeAndParse<HnDocumentUploadLinkResponse>(
      hnCreateDocumentUploadLinkMutation,
      { input },
      'hnCreateDocumentUploadLink'
    );
  }

  async hnEndDocumentUploadSession(
    input: DeepPartial<HnEndDocumentUploadSessionInput>
  ): Promise<HnDocumentUploadSessionResponse> {
    return this.executeAndParse<HnDocumentUploadSessionResponse>(
      hnEndDocumentUploadSessionMutation,
      { input },
      'hnEndDocumentUploadSession'
    );
  }

  async hnValidateAddress(input: HnValidateAddressInput): Promise<HnAddressValidationResponse> {
    // Pre-auth onboarding users (invitation-code flow) have no Cognito session.
    // Fall back to anonymous IAM signing so they can still validate addresses.
    return this.executeAndParse<HnAddressValidationResponse>(
      hnValidateAddressMutation,
      { input },
      'hnValidateAddress',
      undefined,
      { useIamAuthFallback: true }
    );
  }

  /**
   * SEE-1232 row q — the disputes list behind the Credit page. Read-only; the caller
   * paginates by looping until `nextToken` is null, unchanged from the Amplify path.
   */
  async listHighnoteDisputeEventsByPaidolId<T>(variables: {
    paidolId: string;
    sortDirection?: string;
    limit?: number;
    nextToken?: string | null;
  }): Promise<{ items?: T[]; nextToken?: string | null } | undefined> {
    const result = await this.execute<{
      listHighnoteDisputeEventsByPaidolId: { items?: T[]; nextToken?: string | null };
    }>(listHighnoteDisputeEventsByPaidolIdQuery, variables, 'listHighnoteDisputeEventsByPaidolId');
    return result.listHighnoteDisputeEventsByPaidolId;
  }

  // --- Health Check ---

  async v2HighnoteHealthCheck(input: string): Promise<string> {
    const result = await this.execute<{ v2HighnoteHealthCheck: string }>(
      v2HighnoteHealthCheckQuery,
      { input },
      'v2HighnoteHealthCheck'
    );
    return result.v2HighnoteHealthCheck;
  }

  // --- Private helpers ---

  /**
   * Execute a GraphQL operation and extract the result by operation name.
   * Returns the structured response directly (typed returns, no JSON parsing needed).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async executeAndParse<T>(
    query: string,
    variables: object,
    operationName: string,
    customHeaders?: Record<string, string>,
    options: { useIamAuthFallback?: boolean } = {}
  ): Promise<T> {
    const result = await this.execute<Record<string, T>>(
      query,
      variables,
      operationName,
      customHeaders,
      options
    );
    return result[operationName] ?? (null as unknown as T);
  }

  private async execute<T>(
    query: string,
    variables: object,
    operationName: string,
    customHeaders?: Record<string, string>,
    options: { useIamAuthFallback?: boolean } = {}
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'highnote', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const body = JSON.stringify({ query, variables });
      const response = await this.fetchWithAuth(body, customHeaders, options.useIamAuthFallback ?? false);

      let json: { data?: T; errors?: Array<{ message: string }> };
      try {
        json = await response.json();
      } catch {
        throw new Error(`HTTP ${response.status}: non-JSON response`);
      }

      if (!response.ok) {
        const message =
          json?.errors?.map((e: { message: string }) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          tags: { area: 'highnote', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
          extra: {
            operation: operationName,
            endpoint: this.cdkAppSyncEndpoint,
          },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Try Cognito User Pool JWT first; if no session exists and the caller has
   * opted in via `useIamAuthFallback`, sign the request with anonymous IAM
   * credentials from the Cognito Identity Pool. Used by `hnValidateAddress` so
   * pre-auth invitation-code users on the onboarding pages can still validate
   * addresses (the AppSync mutation is also marked `@aws_iam`, and the unauth
   * IAM role is granted access in the CDK runtime stack).
   */
  private async fetchWithAuth(
    body: string,
    customHeaders: Record<string, string> | undefined,
    useIamAuthFallback: boolean
  ): Promise<Response> {
    try {
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      // retries: 0 — ACH transfers and other Highnote mutations are not idempotent
      // and carry no client token, so a request that landed but lost its response
      // must not be re-sent. Still classifies/tags transport failures (SEE-1424 review).
      return fetchWithTransportRetry(
        this.cdkAppSyncEndpoint,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: jwt,
            ...customHeaders,
          },
          body,
        },
        { retries: 0 }
      ).then((response) => {
        // SEE-1738: report a refusal of the Cognito request only; the IAM fallback
        // below never does. Read a clone so execute() still parses the original.
        try {
          response
            .clone()
            .json()
            .then(
              (json: { errors?: unknown }) => reportCdkRefusal(json?.errors, { authMode: 'cognito' }),
              () => undefined
            );
        } catch {
          // Not a readable Response; nothing to report.
        }
        return response;
      });
    } catch (err) {
      if (!useIamAuthFallback) throw err;
      // Fall back to anonymous IAM auth only when there's genuinely no Cognito
      // session — not for transient failures (network, refresh-token errors,
      // Amplify misconfiguration) which would silently downgrade an authenticated
      // user to IAM scope.
      if (!isNoCognitoSessionError(err)) {
        captureException(err, {
          tags: { area: 'highnote', type: 'auth' },
          extra: { reason: 'currentSession-failed-but-not-no-session', useIamAuthFallback },
        });
        throw err;
      }
      return this.executeWithIamAuth(body, customHeaders);
    }
  }

  private async executeWithIamAuth(body: string, customHeaders?: Record<string, string>): Promise<Response> {
    const credentials = await Auth.currentCredentials();
    const { accessKeyId, secretAccessKey, sessionToken } = credentials;

    const url = new URL(this.cdkAppSyncEndpoint);
    // Extract region from AppSync endpoint: *.appsync-api.{region}.amazonaws.com
    const regionMatch = url.hostname.match(/\.appsync-api\.([^.]+)\./);
    const region = regionMatch?.[1] ?? 'us-east-2';

    const request = {
      method: 'POST',
      url: this.cdkAppSyncEndpoint,
      headers: {
        'content-type': 'application/json',
        ...customHeaders,
        host: url.host,
      },
      data: body,
    };

    const signedRequest = Signer.sign(
      request,
      { access_key: accessKeyId, secret_key: secretAccessKey, session_token: sessionToken },
      { region, service: 'appsync' }
    );

    // retries: 0 — see fetchWithAuth: non-idempotent money movement must stay single-shot.
    return fetchWithTransportRetry(
      this.cdkAppSyncEndpoint,
      {
        method: 'POST',
        headers: signedRequest.headers,
        body,
      },
      { retries: 0 }
    );
  }
}

// Export a singleton instance for convenience
export const highnoteService = new HighnoteService();
